import { useMemo, useRef, useState } from 'react';
import { required } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useRunRequest } from '@/hooks';
import { TurnResourcesEnum, TurnTargetsEnum } from '@/types/turn-enums';
import { RequestMVRProps } from '../types';
import { requiredFields } from './constants';
import { isSameProvider, licenseValidate } from './utils';
import { OverrideProviderConfirmState, RequestMVRFormFields, RequestMVRPayload } from './types';

const getChangeValue = (eventOrValue: unknown): string => {
  if (eventOrValue && typeof eventOrValue === 'object' && 'target' in eventOrValue) {
    const value = (eventOrValue as { target?: { value?: unknown } }).target?.value;
    return value == null ? '' : `${value}`;
  }

  return eventOrValue == null ? '' : `${eventOrValue}`;
};

const useController = ({ onClose }: RequestMVRProps) => {
  const { handleSubmit, setValue, watch } = useFormContext<RequestMVRFormFields>();
  const { runRequest } = useRunRequest();
  const { id: workerId } = useParams<{ id: string }>();
  const confirmedProviderRef = useRef<string | null>(null);
  const [overrideConfirm, setOverrideConfirm] = useState<OverrideProviderConfirmState>({
    open: false,
    defaultProvider: '',
    overrideProvider: '',
    state: '',
  });

  const fieldDisabled = true;

  const values = watch(requiredFields);

  const requestAlwaysEnable = useMemo(
    () => Object.values(values).every((value) => value !== undefined && value !== null && value !== ''),
    [values]
  );

  const validateLicense = [required(), licenseValidate];

  const getPayload = (): RequestMVRPayload => {
    // Use watch() instead of handleSubmit values: MVR fields are disabled, and
    // react-hook-form omits disabled inputs from submit values.
    const currentValues = watch(requiredFields);
    const provider = watch('provider');

    const payload = requiredFields.reduce((acc, key, index) => {
      const value = currentValues[index];

      // Display-only: used for override confirmation, not sent to do_mvr.
      if (key === 'default_mvr_provider') {
        return acc;
      }

      if (key === 'drivers_license_state') {
        acc.state = value;
      } else {
        acc[key] = value;
      }

      return acc;
    }, {} as RequestMVRPayload);

    if (provider) {
      payload.provider = `${provider}`;
    }

    return payload;
  };

  const handleRequest = async () => {
    await runRequest({
      fetchType: 'CREATE',
      resource: TurnResourcesEnum.FcraWorkers,
      params: {
        id: workerId,
        target: TurnTargetsEnum.DoMvr,
        data: getPayload(),
      },
      mode: 'notify',
      notify: {
        onError: { message: 'Error: MVR Search was not initiated' },
        onSuccess: { message: 'MVR Requested Successfully', postAction: onClose },
      },
    });
  };

  const handleOverrideProviderChange = (...event: unknown[]) => {
    const nextProvider = getChangeValue(event[0]);

    if (!nextProvider) {
      confirmedProviderRef.current = null;
      return;
    }

    if (nextProvider === confirmedProviderRef.current) {
      return;
    }

    const defaultProvider = watch('default_mvr_provider') || '';

    if (isSameProvider(nextProvider, defaultProvider)) {
      confirmedProviderRef.current = nextProvider;
      return;
    }

    setOverrideConfirm({
      open: true,
      defaultProvider,
      overrideProvider: nextProvider,
      state: watch('drivers_license_state') || '',
    });
  };

  const handleOverrideConfirm = () => {
    confirmedProviderRef.current = overrideConfirm.overrideProvider;
    setOverrideConfirm((prev) => ({ ...prev, open: false }));
  };

  const handleOverrideCancel = () => {
    confirmedProviderRef.current = null;
    setValue('provider', null);
    setOverrideConfirm((prev) => ({ ...prev, open: false, overrideProvider: '' }));
  };

  return {
    fieldDisabled,
    handleOverrideCancel,
    handleOverrideConfirm,
    handleOverrideProviderChange,
    handleRequest: handleSubmit(handleRequest),
    overrideConfirm,
    requestAlwaysEnable,
    validateLicense,
  };
};

export default useController;
